import { ExclamationTriangleIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import { formatCaregiverLocation, pathNameToBreadCrumbLinks } from "@utils/helpers";
import { getDifferenceInYearsAndMonths, unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import parsePhoneNumberFromString from "libphonenumber-js";
import React, { useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import { Breadcrumb, Headline, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";

import { useGetClinicLeadsQuery } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

enum CopiedFieldType {
  Phone = "phone",
  Email = "email",
}

export const LeadDetail: React.FC = () => {
  const { pathname } = useLocation();
  const { leadId } = useParams<{ leadId: string }>();
  const [searchParams] = useSearchParams();
  const isPublic = searchParams.get("public") === "true";
  const breadcrumbLinks = pathNameToBreadCrumbLinks(pathname);

  const [copiedField, setCopiedField] = useState<CopiedFieldType | null>(null);

  const { data, isLoading } = useGetClinicLeadsQuery(
    { filter: { ...(isPublic ? { publicLeadId: leadId } : { leadId: leadId }) } },
    { queryKey: ["getClinicLeadByID", leadId] },
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center">
        <div className="flex items-center gap-3">
          <Paragraph colorType="secondary">Loading...</Paragraph>
        </div>
      </div>
    );
  }

  const clinicLead = unwrapConnection(data?.clinicLeads)?.[0];
  const caregiverMetaData = clinicLead?.caregiver?.caregivermetadata;
  const learnerMetadata = clinicLead?.learner.learnermetadata;
  const learnerName = learnerMetadata ? `${learnerMetadata.firstName} ${learnerMetadata.lastName}`.trim() : "—";
  const parentName = caregiverMetaData ? `${caregiverMetaData.firstName} ${caregiverMetaData.lastName}`.trim() : "—";
  const parentLocation = caregiverMetaData ? formatCaregiverLocation(caregiverMetaData) || "—" : "—";
  const phoneText =
    caregiverMetaData?.phoneNumber1 != null
      ? parsePhoneNumberFromString(caregiverMetaData.phoneNumber1, "US")?.format("NATIONAL") ??
        caregiverMetaData.phoneNumber1
      : "";
  const emailText = caregiverMetaData?.email ?? "";
  const childAge = learnerMetadata?.birthDate
    ? getDifferenceInYearsAndMonths(learnerMetadata.birthDate, new Date())
    : "";

  const requestedServices = clinicLead?.requestedServiceTypes?.map(convertReadableString).join(", ") || "—";
  const requestedModalities = clinicLead?.requestedModalityTypes?.map(convertReadableString).join(", ") || "—";
  const insurance = convertReadableString(clinicLead?.payor) || "—";

  const showCopiedTooltip = (field: CopiedFieldType) => {
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1000);
  };

  const handleCopyPhone = () => {
    if (phoneText) {
      navigator.clipboard.writeText(phoneText);
      showCopiedTooltip(CopiedFieldType.Phone);
    }
  };

  const handleCopyEmail = () => {
    if (emailText) {
      navigator.clipboard.writeText(emailText);
      showCopiedTooltip(CopiedFieldType.Email);
    }
  };

  return (
    <div className="flex flex-col items-center mt-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          <Breadcrumb
            links={breadcrumbLinks}
            homeLink={{
              name: "Home",
              Component: <Link to={`/`}>Home</Link>,
              current: false,
              type: "component",
            }}
          />
          <Headline size="medium">
            Lead from <span className="ph-no-capture">{parentLocation}</span>
          </Headline>
          {clinicLead?.createdDate && (
            <Paragraph size="medium">
              Submitted: {format(new Date(clinicLead?.createdDate), "EEE, MMM d yyyy, h:mma")}
            </Paragraph>
          )}
        </div>

        <div className="flex flex-row gap-2 items-start rounded-lg p-4 bg-yellow-50 wrap-break-word">
          <ExclamationTriangleIcon className="size-6 shrink-0 text-yellow-800" />
          <div className="w-full text-left">
            <Paragraph className="text-sm! text-yellow-800!">
              <span className="font-semibold">Important:</span> All follow-up communication happens directly between you
              and the family, outside of Frontera. Please contact them promptly to maintain interest.
            </Paragraph>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-lg border p-8 bg-white">
          <Headline size="medium">Parent Contact Details</Headline>

          <div className="flex flex-col">
            <Paragraph displayType="loud">Name</Paragraph>
            <Paragraph colorType="primary" className="ph-no-capture">
              {parentName}
            </Paragraph>
          </div>

          {phoneText && (
            <div className="flex flex-col">
              <Paragraph displayType="loud">Phone</Paragraph>
              <div className="flex flex-row gap-2 items-center">
                <Paragraph colorType="primary" className="ph-no-capture">
                  {phoneText}
                </Paragraph>
                <Tooltip
                  text={copiedField === CopiedFieldType.Phone ? "Copied!" : "Copy"}
                  showInfoIcon={false}
                  placement="top"
                >
                  <span>
                    <Square2StackIcon className="size-6 text-text-secondary cursor-pointer" onClick={handleCopyPhone} />
                  </span>
                </Tooltip>
              </div>
            </div>
          )}

          {emailText && (
            <div className="flex flex-col">
              <Paragraph displayType="loud">Email</Paragraph>
              <div className="flex flex-row gap-2 items-center">
                <Paragraph colorType="primary" className="ph-no-capture">
                  {emailText}
                </Paragraph>
                <Tooltip
                  text={copiedField === CopiedFieldType.Email ? "Copied!" : "Copy"}
                  showInfoIcon={false}
                  placement="top"
                >
                  <span>
                    <Square2StackIcon className="size-6 text-text-secondary cursor-pointer" onClick={handleCopyEmail} />
                  </span>
                </Tooltip>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-4 rounded-lg border p-8 bg-white flex-1">
            <Headline size="medium">Child Details</Headline>

            <div className="flex flex-col">
              <Paragraph displayType="loud">Name</Paragraph>
              <Paragraph colorType="primary" className="ph-no-capture">
                {learnerName}
              </Paragraph>
            </div>

            {childAge && (
              <div className="flex flex-col">
                <Paragraph displayType="loud">Age</Paragraph>
                <Paragraph colorType="primary" className="ph-no-capture">
                  {childAge} old
                </Paragraph>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 rounded-lg border p-8 bg-white flex-1">
            <Headline size="medium">Service Request</Headline>

            <div className="flex flex-col">
              <Paragraph displayType="loud">Service</Paragraph>
              <Paragraph colorType="primary">{requestedServices}</Paragraph>
            </div>

            {requestedModalities && (
              <div className="flex flex-col">
                <Paragraph displayType="loud">Modality</Paragraph>
                <Paragraph colorType="primary">{requestedModalities}</Paragraph>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-4 rounded-lg border p-8 bg-white flex-1">
            <Headline size="medium">Location</Headline>
            <Paragraph colorType="primary" className="ph-no-capture">
              {parentLocation}
              {caregiverMetaData?.zip && !caregiverMetaData.zip.includes("|") ? ` (zip: ${caregiverMetaData.zip})` : ""}
            </Paragraph>
          </div>

          <div className="flex flex-col gap-4 rounded-lg border p-8 bg-white flex-1">
            <Headline size="medium">Insurance</Headline>

            <div className="flex flex-col">
              <Paragraph colorType="primary">{insurance}</Paragraph>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
